import { createContext, useCallback, useContext, useEffect, useState, type ReactNode } from 'react';
import {
  DEFAULT_EXPIRY_ALERT_DAYS,
  type Settings,
  type ShoppingListEntry,
  type StorageSummary,
} from '@almox/shared';
import { api } from './api/client.js';

interface AppDataValue {
  storages: StorageSummary[];
  settings: Settings;
  shopping: ShoppingListEntry[];
  /** Categorias ja usadas — alimentam o autocompletar de classificacao. */
  categories: string[];
  /** Marcas ja usadas — autocompletar do campo Marca do cadastro. */
  brands: string[];
  pendingCount: number;
  alertCount: number;
  online: boolean;
  ready: boolean;
  refresh: () => Promise<void>;
}

const FALLBACK: Settings = {
  expiryAlertDays: DEFAULT_EXPIRY_ALERT_DAYS,
  lastStorageId: null,
  externalCatalogEnabled: true,
  photoAiEnabled: true,
  // Sem resposta do servidor, a leitura por foto fica escondida ate saber.
  photoAiAvailable: false,
};

const AppDataContext = createContext<AppDataValue>({
  storages: [],
  settings: FALLBACK,
  shopping: [],
  categories: [],
  brands: [],
  pendingCount: 0,
  alertCount: 0,
  online: true,
  ready: false,
  refresh: async () => {},
});

export const useAppData = () => useContext(AppDataContext);

/** Estado compartilhado pelas telas: dispensas, configuracoes e lista de compras. */
export function AppDataProvider({ children }: { children: ReactNode }) {
  const [storages, setStorages] = useState<StorageSummary[]>([]);
  const [settings, setSettings] = useState<Settings>(FALLBACK);
  const [shopping, setShopping] = useState<ShoppingListEntry[]>([]);
  const [categories, setCategories] = useState<string[]>([]);
  const [brands, setBrands] = useState<string[]>([]);
  const [online, setOnline] = useState(true);
  const [ready, setReady] = useState(false);

  const refresh = useCallback(async () => {
    try {
      const [nextStorages, nextSettings, nextShopping, nextCategories, nextBrands] =
        await Promise.all([
          api.listStorages(),
          api.getSettings(),
          api.listShopping(),
          api.listCategories(),
          api.listBrands(),
        ]);
      setStorages(nextStorages);
      setSettings(nextSettings);
      setShopping(nextShopping);
      setCategories(nextCategories);
      setBrands(nextBrands);
      setOnline(true);
    } catch {
      setOnline(false);
    } finally {
      setReady(true);
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const alertCount = storages.reduce(
    (total, storage) => total + storage.expiringCount + storage.expiredCount,
    0,
  );

  return (
    <AppDataContext.Provider
      value={{
        storages,
        settings,
        shopping,
        categories,
        brands,
        pendingCount: shopping.filter((entry) => entry.status === 'pending').length,
        alertCount,
        online,
        ready,
        refresh,
      }}
    >
      {children}
    </AppDataContext.Provider>
  );
}
